
import { motion } from 'framer-motion';

export default function Hero() {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Cool floating animation for background elements
  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 100,
      scale: 0.8
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        duration: 1.2
      }
    }
  };

  const buttonVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.5,
      rotateX: -90
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 200,
        delay: 0.8
      }
    },
    hover: {
      scale: 1.05,
      y: -5,
      boxShadow: "0 20px 40px rgba(255, 102, 0, 0.3)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden pt-16 sm:pt-20"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 51, 102, 0.7), rgba(0, 51, 102, 0.7)), url('https://public.readdy.ai/ai/img_res/b0c82cd3c0d74ffd23b31f34806c3406.jpg')`
      }}
    >
      {/* Animated background elements */}
      <motion.div 
        className="absolute top-20 left-10 w-4 h-4 bg-accent rounded-full opacity-60"
        variants={floatingVariants}
        animate="animate"
      />
      <motion.div 
        className="absolute top-40 right-20 w-6 h-6 bg-white rounded-full opacity-40"
        variants={floatingVariants}
        animate="animate"
        style={{ animationDelay: '2s' }}
      />
      <motion.div 
        className="absolute bottom-40 left-20 w-3 h-3 bg-accent-300 rounded-full opacity-50"
        variants={floatingVariants}
        animate="animate"
        style={{ animationDelay: '4s' }}
      />

      <div className="container mx-auto px-4 sm:px-6 text-center text-white relative z-10 py-12 sm:py-0">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-2"
            variants={textVariants}
            initial="hidden"
            animate="visible"
          >
            Medium Speed{" "}
            <motion.span 
              className="text-accent inline-block"
              initial={{ opacity: 0, rotateY: -90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              transition={{ delay: 0.5, duration: 0.8, type: "spring" }}
            >
              Diesel & HFO Generators
            </motion.span>
          </motion.h1>
          
          <motion.p 
            className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-gray-200 leading-relaxed px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 0.4, 
              duration: 0.8,
              type: "spring",
              stiffness: 100
            }}
          >
            Leading provider of medium speed diesel and HFO (Heavy Fuel Oil) generator services, repairs, sales, and maintenance. 
            Keeping your operations powered with expert technical support and trusted solutions.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <motion.button 
              onClick={scrollToContact}
              className="bg-accent text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              Get Free Quote
            </motion.button>
            <motion.button 
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-primary transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              whileHover={{
                scale: 1.05,
                y: -5,
                backgroundColor: "rgba(255, 255, 255, 1)",
                color: "#003366",
                boxShadow: "0 20px 40px rgba(255, 255, 255, 0.2)",
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }
              }}
              whileTap={{ scale: 0.95 }}
              style={{ delay: 0.1 }}
            >
              Our Services
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
      
      <motion.div 
        className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: 1, 
          y: 0,
          transition: { delay: 1.2, duration: 0.6 }
        }}
      >
        <motion.i 
          className="ri-arrow-down-line text-white text-xl sm:text-2xl"
          animate={{
            y: [0, 10, 0],
            transition: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
        />
      </motion.div>
    </section>
  );
}
